import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateVehicleFuelType, useUpdateVehicleFuelType } from "../../../../../common/api/queries/vehicleFuelType";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { FuelType } from "../../../../contract/enums";
import { FuelNameSource } from "../../enums";
import { CertificateFuelType, CreateUpdateCertificateFuelType } from "../../types";

interface Props {
  fuelType?: CertificateFuelType;
  open: boolean;
  onFormCancel: () => void;
}

export const VehicleFuelTypeForm = ({ fuelType, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const createVehicleFuelType = useCreateVehicleFuelType();
  const updateVehicleFuelType = useUpdateVehicleFuelType();

  const [form] = Form.useForm<CreateUpdateCertificateFuelType>();

  useEffect(() => {
    if (open && fuelType) {
      form.setFieldsValue(fuelType);
    }
  }, [fuelType, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (fuelType) {
          updateVehicleFuelType.mutate(
            { id: fuelType.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "vehicleFuelType.attrs", error.violations),
            },
          );
        } else {
          createVehicleFuelType.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "vehicleFuelType.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={fuelType ? t("vehicleFuelType.titles.updateFuelType") : t("vehicleFuelType.titles.createFuelType")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createVehicleFuelType.isPending || updateVehicleFuelType.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={fuelType} />

      <Form form={form} layout="vertical" name="vehicleFuelTypeForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item name="fuelType" label={t("contract.enums.fuelType._label")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(FuelType).map((fuelType) => ({
                  value: fuelType,
                  label: t("contract.enums.fuelType." + fuelType),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="name"
              label={t("vehicleFuelType.attrs.name")}
              rules={[
                validations.notNull,
                validations.size(1, 255),
                validations.pattern(regexPatterns.vehicleFuelTypeNameRegex),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="source" label={t("vehicle.enums.fuelNameSource._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                options={Object.keys(FuelNameSource).map((fuelNameSource) => ({
                  value: fuelNameSource,
                  label: t("vehicle.enums.fuelNameSource." + fuelNameSource),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
